import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Lock, AlertTriangle, User, Building2, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import PasswordInput from "@/components/ui/PasswordInput";
import { apiService } from "../../services/ApiService";

// Test accounts for development
const TEST_ACCOUNTS = [
  {
    email: "guest@saunadip.com",
    password: "password",
    role: "Guest",
    icon: User,
    description: "Guest account"
  },
  {
    email: "operator@saunadip.com", 
    password: "password",
    role: "Operator",
    icon: Building2,
    description: "Sauna operator account"
  },
  {
    email: "admin@saunadip.com",
    password: "password", 
    role: "Admin",
    icon: Shield,
    description: "Administrator account"
  }
];

function WarmAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-3 py-2 mt-2 shadow-sm text-sm">
      <AlertTriangle className="w-4 h-4 text-orange-400" />
      <span>{message}</span>
    </div>
  );
}

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const selectTestAccount = (account: typeof TEST_ACCOUNTS[0]) => {
    setFormData({
      email: account.email,
      password: account.password,
    });
  };

  const handleResend = async () => {
    setIsResending(true);
    setResendMessage("");
    try {
      const response = await apiService.post('resend-verification');
      if (response.ok) {
        setResendMessage("Verification email resent! Please check your inbox.");
      } else {
        setResendMessage("Failed to resend verification email. Please try again later.");
      }
    } catch (error) {
      setResendMessage("An error occurred. Please try again later.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setVerificationError("");
    
    try {
      const { user } = await login(formData.email, formData.password);
      
      // Check user role using both 'role' and 'roles' fields
      const isAdmin = user.role === 'admin' || (user.roles && user.roles.includes('admin'));
      const isOperator = user.role === 'operator' || (user.roles && user.roles.includes('operator'));
      
      if (isAdmin) {
        navigate('/admin/metrics');
      } else if (isOperator) {
        navigate('/operator-admin-simple');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      // Handle specific error cases
      if (error.message.includes("verify your email")) {
        setVerificationError("Please verify your email address before logging in.");
      } else {
        toast({
          title: "Login failed",
          description: error.message || "Please check your credentials and try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-sauna-beige min-h-screen flex items-start justify-center px-2 mt-4">
      <Card className="w-full max-w-md min-h-[500px] shadow-2xl border-none rounded-2xl p-0 sm:p-1 bg-white/90 flex flex-col justify-center">
        <CardHeader className="flex flex-col items-center pt-6 pb-4">
          <div className="rounded-full bg-gradient-to-b from-sauna-orange/80 to-sauna-orange p-4 mb-4 shadow-lg">
            <Lock className="h-10 w-10 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-sauna-navy mb-1 text-center">Sign In</CardTitle>
          <CardDescription className="text-base text-sauna-navy/80 mb-2 text-center font-medium">Access your account</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-6 pt-0">
          {/* Test Account Selector */}
          <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-xs font-semibold text-blue-800 mb-1">🧪 Test Accounts</h3>
            <div className="flex gap-1">
              {TEST_ACCOUNTS.map((account) => {
                const Icon = account.icon;
                return (
                  <button
                    key={account.email}
                    type="button"
                    onClick={() => selectTestAccount(account)}
                    className="flex-1 flex items-center gap-1 p-1.5 text-left bg-white border border-blue-200 rounded text-xs hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    title={`${account.role}: ${account.email}`}
                  >
                    <Icon className="w-3 h-3 text-blue-600 flex-shrink-0" />
                    <span className="text-blue-800 font-medium truncate">{account.role}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email" className="text-sauna-navy font-semibold mb-1 block">Email</Label>
              <Input 
                id="email" 
                name="email" 
                type="email" 
                placeholder="you@example.com" 
                value={formData.email} 
                onChange={handleChange} 
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg" 
                required 
              />
              {verificationError && <WarmAlert message={verificationError} />}
            </div>
            <div>
              <Label htmlFor="password" className="text-sauna-navy font-semibold mb-1 block">Password</Label>
              <PasswordInput
                id="password"
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-base py-2 rounded-lg"
                required
              />
            </div>
            <div className="flex justify-start mb-2 mt-1">
              <Link to="/forgot-password" className="text-xs text-sauna-orange hover:underline">Forgot password?</Link>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white text-lg py-2 mt-2 rounded-lg shadow-md hover:from-sauna-orange/90 hover:to-sauna-orange transition font-bold tracking-wide disabled:opacity-70 disabled:cursor-not-allowed" 
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Log In"}
            </Button>
            <div className="text-center mt-4 text-sauna-navy/80 text-base">
              Don't have an account? <Link to="/choose-signup" className="text-sauna-orange font-semibold hover:underline">Sign up</Link>
            </div>
            <div className="text-xs text-center text-sauna-navy/60 mt-6">
              By logging in, you agree to our <Link to="/terms-of-use" className="text-sauna-orange underline">Terms of Use</Link> and <Link to="/privacy-policy" className="text-sauna-orange underline">Privacy Policy</Link>.
            </div>
          </form>
          
          {/* Email verification error message */}
          {verificationError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 mb-2">{verificationError}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResend}
                  disabled={isResending}
                  className="text-xs"
                >
                  {isResending ? "Sending..." : "Resend verification email"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/verify-email')}
                  className="text-xs"
                >
                  Go to verification page
                </Button>
              </div>
              {resendMessage && (
                <p className="text-xs text-green-600 mt-2">{resendMessage}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginForm;
